import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as notesApi from 'lib/api/notes';
import { uploadFileDocuments, uploadImages } from 'utils/uploadStreamFiles';
import If from 'global/Common/If';
import debounce from 'lodash.debounce';
import Header from './Header';
import Textarea from './Textarea';
import Footer from './Footer';
import { ImageType, FileType, SavedStateType } from './types';
import Attachment from './Attachment';
import { MAX_NOTES_LENGTH } from './constants';
import { parseExistImages } from './utils';
import { ContentContainer, NoteSkeletonContainer, NoteSkeletonContent, NoteSkeletonSubtitle, NoteSkeletonTitle } from './styled';

interface NotesComposeProps {
  defaultSymbol: string;
  companyName: string;
}

const NotesCompose = ({
  defaultSymbol,
  companyName,
}: NotesComposeProps) => {
  const savedState = useRef<SavedStateType>('Draft');
  const isChanged = useRef(false);
  const isSaving = useRef(false);
  const previousContentRef = useRef<string>('');

  const [notesId, setNotesId] = useState(null);
  const [content, setContent] = useState('');
  const [documents, setDocuments] = useState<FileType[]>([]);
  const [images, setImages] = useState<ImageType[]>([]);

  const { isLoading } = useQuery({
    queryKey: ['notes-load', defaultSymbol],
    queryFn: () => notesApi.getNotes({ company_symbol: defaultSymbol }),
    onSuccess: ({ data }) => {
      const notesData = data.data.notes[0];

      if (notesData) {
        if (
          content.length === 0 &&
          images.length === 0 &&
          documents.length === 0
        ) {
          previousContentRef.current = notesData.content.text;
          const documentList: FileType[] = notesData.attachment.file_urls.map((item) => ({
            fileUrl: item,
            preview: item,
            isNew: false,
          }));

          setContent(notesData.content.text);

          // NOTE : Uncomment if product decided to allow usage of upload picture/document
          // setImages(parseExistImages(notesData.attachment.image_urls));
          // setDocuments(documentList);
        }
        setNotesId(notesData.id);
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
  });

  const handleUploadFiles = async () => {
    const newImageList = images.filter((item) => !item.isNew);
    const newDocumentList = documents.filter((item) => !item.isNew);

    try {
      const needUploadedImages = images.filter((item) => item.isNew);
      const needUploadedDocuments = documents.filter((item) => item.isNew);

      const uploadedImages =
        needUploadedImages.length > 0 && await uploadImages(needUploadedImages);

      const uploadedDocuments =
        needUploadedDocuments.length > 0 && await uploadFileDocuments(needUploadedDocuments);

      const { data: listOfImageUrl = [] } = uploadedImages;
      const { data: listOfDocumentUrl = [] } = uploadedDocuments;

      if (listOfImageUrl.length > 0) {
        listOfImageUrl.forEach((image) => {
          newImageList.push({
            preview: image,
            file: image,
            isNew: false,
          });
        });
      }
      if (listOfDocumentUrl.length > 0) {
        listOfDocumentUrl.forEach((image) => {
          newDocumentList.push({
            preview: image,
            fileUrl: image,
            isNew: false,
          });
        });
      }
    } catch (error) {
      return { error };
    }
    return { newImageList, newDocumentList };
  };

  const saveNoteMutation = useMutation({
    mutationFn: async () => {
      if (content === previousContentRef.current) return null;

      isSaving.current = true;
      previousContentRef.current = content;

      const { newImageList, newDocumentList } = await handleUploadFiles();
      const listImage = newImageList.map((item) => item.preview);

      setImages(newImageList);
      setDocuments(newDocumentList);

      if (notesId) {
        // DELETE NOTE
        if (content.length === 0) {
          await notesApi.deleteNotes(notesId);
          return { action: 'DELETE' };
        }

        // UPDATE NOTE
        const putPayload: PutNotesPayload = {
          note: content,
          image_urls: listImage,
          file_urls: newDocumentList.map((item) => item.preview),
        };
        await notesApi.putNotes(putPayload, notesId);
        return { action: 'UPDATE' };
      }

      // CREATE NOTE
      const postPayload: PostNotesPayload = {
        company_symbol: defaultSymbol,
        note: content,
        image_urls: listImage,
        file_urls: newDocumentList.map((item) => item.preview),
      };
      await notesApi.postNotes(postPayload);
      const response = await notesApi.getNotes({ company_symbol: defaultSymbol });
      const notesData = response.data?.data?.notes[0];

      if (notesData) {
        setNotesId(notesData.id);
      }
      return { action: 'CREATE' };
    },
    onSuccess: (data) => {
      isSaving.current = false;
      savedState.current = 'Saved';
      isChanged.current = false;

      if (content === previousContentRef.current) {
        isChanged.current = false;
      } else {
        isChanged.current = true;
      }

      if (data.action === 'DELETE') {
        setNotesId(null);
      }
    },
    onError: () => {
      isSaving.current = false;
    },
  });

  const forceSave = (e) => {
    if (isChanged.current && !isSaving.current) {
      e.preventDefault();
      saveNoteMutation.mutate();
    }
  };

  useLayoutEffect(() => {
    window.addEventListener('beforeunload', forceSave);

    return () => {
      if (!isSaving.current && isChanged.current) {
        saveNoteMutation.mutate();
      }
      window.removeEventListener('beforeunload', forceSave);
    };
  }, []);

  const autoSave = useCallback(debounce(() => {
    if (!isSaving.current && isChanged.current) {
      saveNoteMutation.mutate();
    }
  }, 5000), []);

  const handleChangeContent = (text: string) => {
    isChanged.current = true;
    savedState.current = 'Draft';
    if (text.length <= MAX_NOTES_LENGTH) {
      setContent(text);
    } else {
      const clippedText = text.slice(0, 1000);
      setContent(clippedText);
    }
    autoSave();
  };

  return (
    <div>
      <Header
        symbol={defaultSymbol}
        name={companyName}
      />
      <ContentContainer>
        <If condition={!isLoading}>
          <Textarea
            onChange={handleChangeContent}
            value={content}
          />
          <Attachment
            images={images}
            documents={documents}
            onSetImages={(listImage) => {
              setImages(listImage);
              isChanged.current = true;
            }}
            onSetDocuments={(listDocument) => {
              setDocuments(listDocument);
              isChanged.current = true;
            }}
          />
        </If>
        <If condition={isLoading}>
          <NoteSkeletonContainer>
            <NoteSkeletonTitle />
            <NoteSkeletonSubtitle />
            <NoteSkeletonSubtitle />
            <NoteSkeletonContent />
          </NoteSkeletonContainer>
        </If>
      </ContentContainer>
      <Footer
        length={content?.length}
        documents={documents}
        images={images}
        isLoading={isLoading}
        onSetImages={(listImage) => {
          setImages(listImage);
          isChanged.current = true;
        }}
        onSetDocuments={(listDocument) => {
          setDocuments(listDocument);
          isChanged.current = true;
        }}
        savedState={savedState.current}
      />
    </div>
  );
};

export default NotesCompose;
